// frontend/src/farm/moonPhase.mjs
// The moon's phase changes night by night through 8 phases:
//   0 new · 1 waxing crescent · 2 first quarter · 3 waxing gibbous · 4 full ·
//   5 waning gibbous · 6 last quarter · 7 waning crescent
// As seen from the northern hemisphere (Poland): waxing is lit on the RIGHT, waning on the
// left. Pure (node --test); the renderer (farm/moonPhaseTextures.js) turns litPolygon into
// the eight moon textures.
export const PHASES = 8;

export function moonPhaseFor(night) {
  return ((night % PHASES) + PHASES) % PHASES;
}

// The moon rises at 225 s, late in the evening of cycle n, and sets in the morning of cycle
// n + 1: that whole appearance is night n + 1, so its phase does not flip at midnight.
export function moonNight(t, night) {
  return t >= 180 ? night + 1 : night;
}

const angle = (phase) => (phase / PHASES) * Math.PI * 2;

// Share of the disc that is lit, 0 (new) .. 1 (full).
export function illumination(phase) {
  return (1 - Math.cos(angle(phase))) / 2;
}

// The lit part of a disc of radius r centred on (cx, cy), as a polygon [[x, y], …] (y down).
// It is bounded by the lit limb (a half circle) and the terminator, a half ellipse whose
// x-radius is r·|cos φ| — bulging toward the lit side for a crescent, away from it for a
// gibbous moon. Empty for the new moon. `limbR` pushes only the lit limb out (for a halo that
// hugs the lit edge): the terminator stays on the disc, so nothing reaches the dark side.
export function litPolygon(phase, r, cx = 0, cy = 0, steps = 90, limbR = r) {
  const p = moonPhaseFor(phase);
  if (p === 0) return [];
  const phi = angle(p);
  const side = phi < Math.PI ? 1 : -1;   // waxing: lit on the right
  const k = Math.cos(phi);
  const pts = [];
  for (let i = 0; i <= steps; i++) {          // limb, top → bottom
    const a = -Math.PI / 2 + (Math.PI * i) / steps;
    pts.push([cx + side * limbR * Math.cos(a), cy + limbR * Math.sin(a)]);
  }
  for (let i = steps - 1; i > 0; i--) {        // terminator, bottom → top
    const a = -Math.PI / 2 + (Math.PI * i) / steps;
    pts.push([cx + side * k * r * Math.cos(a), cy + r * Math.sin(a)]);
  }
  return pts;
}

// The glow around the moon: the lit shape with its limb pushed out to `push`·r. The full moon's
// "terminator" is its other limb, so it glows all the way round (litPolygon alone would only
// push one side out — a glow on the left half and none on the right).
export function haloPolygon(phase, r, cx = 0, cy = 0, steps = 90, push = 1.3) {
  const p = moonPhaseFor(phase);
  if (p === 0) return [];
  if (p === PHASES / 2) {
    return Array.from({ length: 2 * steps }, (_, i) => {
      const a = (Math.PI * i) / steps;
      return [cx + push * r * Math.cos(a), cy + push * r * Math.sin(a)];
    });
  }
  return litPolygon(p, r, cx, cy, steps, r * push);
}
